import { type MouseEvent, memo, useEffect, useRef } from 'react';

import { RICH_TEXT_PROSE_CLASS } from '@/components/editor/inline-rich-text-surface';
import { usePreviewInternalLinkClick } from '@/components/editor/use-preview-internal-link-click';
import { isTrustedElfsightScriptSrc } from '@/lib/embed/trusted-elfsight';
import { cn } from '@/lib/utils';

interface RawHtmlBlockProps {
  html?: string;
  interactive?: boolean;
}

export const RawHtmlBlock = memo(function RawHtmlBlock({
  html,
  interactive = true,
}: RawHtmlBlockProps) {
  const content = html?.trim();
  const handlePreviewLinkClick = usePreviewInternalLinkClick();
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!content) return;
    containerRef.current?.querySelectorAll('script[src]:not([data-activated])').forEach((inert) => {
      const src = inert.getAttribute('src') ?? '';
      if (!isTrustedElfsightScriptSrc(src)) return;

      const script = inert.ownerDocument.createElement('script');
      script.src = src;
      script.async = true;
      script.dataset.activated = '';
      inert.replaceWith(script);
    });
  }, [content]);

  if (!content && interactive) return null;

  const handleClickCapture = (event: MouseEvent<HTMLDivElement>) => {
    if (!interactive) {
      if (!(event.target instanceof Element)) return;
      const link = event.target.closest('a[href], area[href]');
      if (link && event.currentTarget.contains(link)) event.preventDefault();
      return;
    }
    handlePreviewLinkClick(event);
  };

  return (
    <div
      ref={containerRef}
      className={cn(
        'raw-html-block w-full',
        RICH_TEXT_PROSE_CLASS,
        !interactive && 'pointer-events-none min-h-24',
      )}
      onClickCapture={handleClickCapture}
      // biome-ignore lint/security/noDangerouslySetInnerHtml: Site-import blocks materialize trusted admin-supplied HTML that must render 1:1 in the parent document
      // biome-ignore lint/style/useNamingConvention: React API requires __html
      dangerouslySetInnerHTML={{ __html: content ?? '' }}
    />
  );
});
